import JobListing from '@/models/JobListing';
import Model from '@/models/Model';
import type { JobListing as IJobListing, JobListingFilter } from '@/types/JobListing';

class JobListingService {
  getJobListings(options?: JobListingFilter) {
    const { page, includes, per_page, sort }: JobListingFilter = options || {
      page: 1,
      per_page: 10,
      includes: ['author'],
      sort: '-created_at',
    };

    return useAsyncData<{data: IJobListing[], meta: any}>(
      `job-listings-${page}-${per_page}-${sort}`,
      async () => {
        const jobListing = new JobListing();
        jobListing.include(includes).limit(per_page).page(page).orderBy(sort);

        const jobListings = await jobListing.get();
        return { data: jobListings, meta: jobListing.meta };
      },
      { server: false }
    );
  }

  getRecommendedJobListings(options?: JobListingFilter) {
    const { page, includes, per_page, sort }: JobListingFilter = options || {
      page: 1,
      per_page: 10,
      includes: ['author'],
      sort: '-created_at',
    };

    return useAsyncData<{data: IJobListing[], meta: any}>(
      `recommended-job-listings-${page}-${per_page}-${sort}`,
      async () => {
        const jobListing = new JobListing();
        jobListing.include(includes).limit(per_page).page(page).orderBy(sort);

        const jobListings = await jobListing.custom('jobs/recommended').get();
        return { data: jobListings, meta: jobListing.meta };
      },
      { server: false }
    );
  }

  getJobDetails(id: number) {
    return useAsyncData<IJobListing>(
      `job-details-${id}`,
      async () => {
        const jobListing = new JobListing();
        const job = await jobListing.find(id);
        const processedJob = Model.processResponse(job) as IJobListing;
        return processedJob;
      },
      { server: false }
    );
  }

  async createJob(jobData: any) {
    const { data, error, execute } = useSubmit();
    
    try {
      await execute('jobs', {
        method: 'POST',
        body: jobData,
      });
    } catch (err) {
      // Error is already handled by useSubmit
    }

    return {
      data,
      error,
    };
  }

  getMyJobs(options?: JobListingFilter) {
    const { page, includes, per_page, sort, is_draft }: JobListingFilter = options || {
      page: 1,
      per_page: 10,
      includes: ['author'],
      sort: '-created_at',
    };

    return useAsyncData<{data: IJobListing[], meta: any}>(
      `my-jobs-${page}-${per_page}-${sort}-${is_draft ? 'draft' : 'published'}`,
      async () => {
        const jobListing = new JobListing();
        jobListing.include(includes).limit(per_page).page(page).orderBy(sort);

        // Add is_draft as a query parameter in the payload
        if (is_draft !== undefined) {
          jobListing.params({ is_draft: is_draft });
        }
        
        const jobListings = await jobListing.custom('jobs/my-jobs').get();
        return { data: jobListings, meta: jobListing.meta };
      },
      { server: false }
    );
  }

  getWorkersAppliedJobs(options?: JobListingFilter) {
    const { page, includes, per_page, sort }: JobListingFilter = options || {
      page: 1,
      per_page: 10,
      includes: ['author'],
      sort: '-created_at',
    };

    return useAsyncData<{data: IJobListing[], meta: any}>(
      `workers-applied-jobs-${page}-${per_page}-${sort}`,
      async () => {
        const jobListing = new JobListing();
        jobListing.include(includes).limit(per_page).page(page).orderBy(sort);
        
        const jobListings = await jobListing.custom('jobs/workers-applied-jobs').get();
        return { data: jobListings, meta: jobListing.meta };
      },
      { server: false }
    );
  }

  getWorkersFavouriteJobs(options?: JobListingFilter) {
    const { page, includes, per_page, sort }: JobListingFilter = options || {
      page: 1,
      per_page: 10,
      includes: ['author'],
      sort: '-created_at',
    };

    return useAsyncData<{data: IJobListing[], meta: any}>(
      `workers-favourite-jobs-${page}-${per_page}-${sort}`,
      async () => {
        const jobListing = new JobListing();
        jobListing.include(includes).limit(per_page).page(page).orderBy(sort);
        
        const jobListings = await jobListing.custom('jobs/workers-favourite-jobs').get();
        return { data: jobListings, meta: jobListing.meta };
      },
      { server: false }
    );
  }

  getWorkersAcceptedJobs(options?: JobListingFilter) {
    const { page, includes, per_page, sort }: JobListingFilter = options || {
      page: 1,
      per_page: 10,
      includes: ['author'],
      sort: '-created_at',
    };

    return useAsyncData<{data: IJobListing[], meta: any}>(
      `workers-accepted-jobs-${page}-${per_page}-${sort}`,
      async () => {
        const jobListing = new JobListing();
        jobListing.include(includes).limit(per_page).page(page).orderBy(sort);
        
        const jobListings = await jobListing.custom('jobs/workers-accepted-jobs').get();
        return { data: jobListings, meta: jobListing.meta };
      },
      { server: false }
    );
  }

  getAppliedWorkersDetails(options?: JobListingFilter) {
    const { page, includes, per_page, sort, job_id }: JobListingFilter = options || {
      page: 1,
      per_page: 10,
      includes: ['author'],
      sort: '-created_at',
      job_id: 0,
    };

    return useAsyncData<any[]>(
      `applied-workers-details-${page}-${per_page}-${sort}-${job_id}`,
      async () => {
        const jobListing = new JobListing();
        jobListing.include(includes).limit(per_page).page(page).orderBy(sort);
        
        // Add job_id as a parameter if provided
        if (job_id) {
          jobListing.params({ job_id: job_id });
        }
        
        const appliedWorkers = await jobListing.custom('jobs/applied-workers-details').get();
        return appliedWorkers || [];
      },
      { server: false }
    );
  }
}

const service = new JobListingService();
export default service;
